/**
 * Exact decimal arithmetic for amounts, areas and formulas. Floating point
 * would print 0.1 + 0.2 as 0.30000000000000004 and could round a rupee the
 * wrong way; deeds need the same digits in the browser and on the server.
 *
 * A Dec is a bigint scaled by 10^12. Every unit conversion factor used here
 * has at most 9 decimals, so conversions to square metres are exact.
 */
export type Dec = bigint;

export const SCALE = 12;
const ONE = 10n ** BigInt(SCALE);

const PLAIN = /^(-)?(\d{1,15})(?:\.(\d{1,12}))?$/;

/** "1,50,000.5" -> Dec. Commas and spaces are ignored. Null if not a number. */
export function parseDec(raw: string): Dec | null {
  const m = PLAIN.exec(raw.replace(/[,\s]/g, ''));
  if (!m) return null;
  const frac = (m[3] ?? '').padEnd(SCALE, '0');
  const v = BigInt(m[2]!) * ONE + BigInt(frac);
  return m[1] ? -v : v;
}

export function decFromInt(n: number): Dec {
  return BigInt(n) * ONE;
}

/** Division of bigints rounded half away from zero. */
function divRound(a: bigint, b: bigint): bigint {
  if (b === 0n) throw new RangeError('division by zero');
  const neg = (a < 0n) !== (b < 0n);
  const [x, y] = [a < 0n ? -a : a, b < 0n ? -b : b];
  const q = (x * 2n + y) / (y * 2n);
  return neg ? -q : q;
}

export const add = (a: Dec, b: Dec): Dec => a + b;
export const sub = (a: Dec, b: Dec): Dec => a - b;
export const mul = (a: Dec, b: Dec): Dec => divRound(a * b, ONE);
/** Null when dividing by zero. */
export function div(a: Dec, b: Dec): Dec | null {
  return b === 0n ? null : divRound(a * ONE, b);
}

export function compare(a: Dec, b: Dec): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Rounded half away from zero to `decimals` places, as plain digits: "1512500.00". */
export function toFixed(v: Dec, decimals: number): string {
  const step = 10n ** BigInt(SCALE - decimals);
  const r = divRound(v, step);
  const neg = r < 0n;
  const abs = neg ? -r : r;
  const s = abs.toString().padStart(decimals + 1, '0');
  const int = s.slice(0, s.length - decimals);
  const frac = decimals ? `.${s.slice(-decimals)}` : '';
  return `${neg ? '-' : ''}${int}${frac}`;
}

/** Indian digit grouping of a plain number string: 15125000.5 -> 1,51,25,000.5 */
export function groupIndianDigits(plain: string): string {
  const m = /^(-?)(\d+)(\.\d+)?$/.exec(plain);
  if (!m) return plain;
  const int = m[2]!;
  const last3 = int.slice(-3);
  const rest = int.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return `${m[1]}${rest ? `${rest},${last3}` : last3}${m[3] ?? ''}`;
}
